/**
 * Asset Path → DB Column Registry — client mirror of
 * `src/server/services/formEngine/assetPathRegistry.ts`.
 *
 * Kept in sync by hand. Used by:
 *   - DynamicAssetFields component: render an input per spec.
 *   - asset-field-requirements client mirror: cross-check baseline +
 *     jurisdiction-extras the workflow gate cares about.
 *
 * MUST stay aligned with the server registry. Adding a new path means
 * updating both files in the same PR.
 */

export const SERVICE_TYPE_ENUM = [
    "fireline",
    "fireline_bypass",
    "irrigation",
    "domestic_irrigation",
    "domestic_fire",
    "domestic_mainline",
    "domestic_other",
    "other",
] as const;

export const MCKINNEY_DEVICE_TYPE_ENUM = [
    "Domestic",
    "Lawn Sprinkler",
    "Commercial",
    "Fire Sprinkler Inside",
    "Fire Sprinkler Outside",
] as const;

export const MCKINNEY_INSTALL_ORIENTATION_ENUM = ["Vertical", "Vertical Down", "Horizontal", "Other"] as const;

export const OCCUPANCY_TYPE_ENUM = ["Residential", "Commercial"] as const;

export const HAZARD_CLASSIFICATION_ENUM = ["health", "non_health"] as const;

export const PROTECTED_SERVICE_CATEGORY_ENUM = [
    "irrigation",
    "co2_soda",
    "fire_sprinkler",
    "point_of_service",
    "other",
] as const;

export type AssetColumnInputType = "enum" | "text" | "boolean";

export interface ShowWhenSpec {
    path: string;
    equals?: unknown;
    notEquals?: unknown;
    in?: unknown[];
}

export interface AssetColumnSpec {
    path: string;
    column: string;
    bodyKey: string;
    label: string;
    inputType: AssetColumnInputType;
    enum?: readonly string[];
    enumLabels?: Record<string, string>;
    showWhen?: ShowWhenSpec;
    aliasPaths?: string[];
}

export const ASSET_PATH_REGISTRY: AssetColumnSpec[] = [
    {
        path: "asset.serves",
        column: "service_type",
        bodyKey: "serviceType",
        label: "What it serves",
        inputType: "enum",
        enum: SERVICE_TYPE_ENUM,
        enumLabels: {
            fireline: "Fireline",
            fireline_bypass: "Fireline Bypass",
            irrigation: "Irrigation",
            domestic_irrigation: "Domestic Irrigation",
            domestic_fire: "Domestic Fire",
            domestic_mainline: "Domestic Mainline",
            domestic_other: "Domestic Other",
            other: "Other",
        },
    },
    {
        path: "asset.mckinney_device_type",
        column: "mckinney_device_type",
        bodyKey: "mckinneyDeviceType",
        label: "McKinney device type",
        inputType: "enum",
        enum: MCKINNEY_DEVICE_TYPE_ENUM,
    },
    {
        path: "asset.mckinney_install_orientation",
        column: "mckinney_install_orientation",
        bodyKey: "mckinneyInstallOrientation",
        label: "Install orientation",
        inputType: "enum",
        enum: MCKINNEY_INSTALL_ORIENTATION_ENUM,
    },
    {
        path: "asset.occupancy_type",
        column: "occupancy_type",
        bodyKey: "occupancyType",
        label: "Occupancy",
        inputType: "enum",
        enum: OCCUPANCY_TYPE_ENUM,
        aliasPaths: ["asset.mckinney_occupancy_type"],
    },
    {
        path: "asset.hazard_type",
        column: "hazard_classification",
        bodyKey: "hazardClassification",
        label: "Hazard classification (Health / Non-Health)",
        inputType: "enum",
        enum: HAZARD_CLASSIFICATION_ENUM,
        enumLabels: { health: "Health", non_health: "Non-Health" },
    },
    {
        path: "asset.hazard_description",
        column: "hazard_description",
        bodyKey: "hazardDescription",
        label: "Description of hazard",
        inputType: "text",
    },
    {
        path: "asset.protected_service_category",
        column: "protected_service_category",
        bodyKey: "protectedServiceCategory",
        label: "Protected service",
        inputType: "enum",
        enum: PROTECTED_SERVICE_CATEGORY_ENUM,
        enumLabels: {
            irrigation: "Irrigation",
            co2_soda: "CO2 / Soda fountain",
            fire_sprinkler: "Fire sprinkler",
            point_of_service: "Point of service",
            other: "Other",
        },
    },
    {
        path: "asset.protected_service_other",
        column: "protected_service_other",
        bodyKey: "protectedServiceOther",
        label: "Protected service — Other (specify)",
        inputType: "text",
        showWhen: { path: "asset.protected_service_category", equals: "other" },
    },
];

const BY_PATH = new Map<string, AssetColumnSpec>();
for (const spec of ASSET_PATH_REGISTRY) {
    BY_PATH.set(spec.path, spec);
    if (spec.aliasPaths) {
        for (const alias of spec.aliasPaths) BY_PATH.set(alias, spec);
    }
}

const BY_COLUMN = new Map<string, AssetColumnSpec>(ASSET_PATH_REGISTRY.map((s) => [s.column, s]));

export function getAssetSpecByPath(path: string): AssetColumnSpec | undefined {
    return BY_PATH.get(path);
}

export function getAssetSpecByColumn(column: string): AssetColumnSpec | undefined {
    return BY_COLUMN.get(column);
}

export function evaluateShowWhen(showWhen: ShowWhenSpec | undefined, valuesByPath: Record<string, unknown>): boolean {
    if (!showWhen) return true;
    const observed = valuesByPath[showWhen.path];
    if (showWhen.equals !== undefined) return observed === showWhen.equals;
    if (showWhen.notEquals !== undefined) return observed !== showWhen.notEquals;
    if (showWhen.in !== undefined) return showWhen.in.includes(observed);
    return true;
}
